import { useEffect, useState } from 'react'
import { Box, Button, Dialog, IconButton, InputAdornment, Stack, Typography } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import FacebookRoundedIcon from '@mui/icons-material/FacebookRounded'
import AppleIcon from '@mui/icons-material/Apple'
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import logo from '../../assets/images/logo.svg'
import FacebookLoginButton from './LoginBySocialmedia/facebook/LoginWithFacebook'
import GoogleLoginButton from './LoginBySocialmedia/google/LoginWithGoogle'
import AppleLoginButton from './LoginBySocialmedia/apple/LoginWithApple'
import BaseForm from '../../formik/BaseForm'
import CustomFormikField from '../../formik/CustomFormikField'
import CountryCodeSelect from '../../formik/CountryCodeSelect'
import PasswordInput from '../../formik/PasswordInput'
import SubmitButton from '../../formik/SubmitButton'
import CheckboxFormik from '../../formik/CheckboxFormik'
import { useLoginValidation } from '../../validation/loginValidation'
import { useSignUpValidation } from '../../validation/signUpValidation'
import { useSubmitForm } from './hooks/loginSubmit'
import { useSignupSubmit } from './hooks/signupSubmit'
import ChannelPickerDialog from './ChannelPickerDialog'
import UsernameField from './UsernameField'

const loginInitialValues = {
  identifier: '',
  password: '',
  countryCode: '+1',
}

const signupInitialValues = {
  firstName: '',
  lastName: '',
  username: '',
  identifier: '',
  password: '',
  countryCode: '+1',
  terms: false,
}

const authChoiceButtonSx = {
  width: '100%',
  minHeight: { xs: 48, sm: 50 },
  px: { xs: 2, sm: 2.4 },
  justifyContent: 'center',
  border: '1px solid rgba(254,31,2,0.6)',
  borderRadius: '6px',
  backgroundColor: '#fff',
  color: '#071F28',
  boxShadow: 'none',
  fontSize: { xs: 15.5, sm: 17 },
  fontWeight: 900,
  textTransform: 'none',
  transition: 'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
  '& .MuiButton-startIcon': { mr: 1.2, ml: 0 },
  '&:hover': {
    borderColor: '#FE1F02',
    backgroundColor: '#fff',
    boxShadow: '0 10px 24px rgba(254,31,2,0.12)',
  },
}

const googleChoiceButtonSx = {
  ...authChoiceButtonSx,
  border: '1px solid #DADCE0',
  color: '#3C4043',
  '&:hover': {
    borderColor: '#C9CCD1',
    backgroundColor: '#F8FAFD',
    boxShadow: '0 10px 24px rgba(60,64,67,0.10)',
  },
}

const GoogleMark = () => (
  <Box
    component="svg"
    aria-hidden="true"
    viewBox="0 0 24 24"
    sx={{
      width: 24,
      height: 24,
      display: 'block',
    }}
  >
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.29h6.47c-.28 1.5-1.13 2.77-2.41 3.62v2.96h3.9c2.28-2.1 3.53-5.2 3.53-8.6z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.95-2.91l-3.9-2.96c-1.08.72-2.46 1.15-4.05 1.15-3.12 0-5.76-2.1-6.71-4.93H1.26v3.05C3.24 21.32 7.29 24 12 24z" />
    <path fill="#FBBC05" d="M5.29 14.35c-.24-.72-.38-1.49-.38-2.35s.14-1.63.38-2.35V6.6H1.26C.46 8.2 0 10.05 0 12s.46 3.8 1.26 5.4l4.03-3.05z" />
    <path fill="#EA4335" d="M12 4.72c1.76 0 3.35.61 4.6 1.8l3.43-3.43C17.95 1.16 15.24 0 12 0 7.29 0 3.24 2.68 1.26 6.6l4.03 3.05C6.24 6.82 8.88 4.72 12 4.72z" />
  </Box>
)

const switchButtonSx = {
  color: '#FE1F02',
  fontWeight: 900,
  textTransform: 'none',
  '&:hover': { backgroundColor: 'transparent', color: '#D91B04' },
}

const formTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    border: '1px solid #D8DEE1',
    backgroundColor: '#fff',
    '&:hover fieldset': { borderColor: 'transparent' },
    '&.Mui-focused': {
      borderColor: '#FE1F02',
      boxShadow: '0 0 0 3px rgba(254,31,2,0.1)',
    },
  },
}

const AuthLoginForm = ({ method, setMethod, onBack, onSuccess }) => {
  const { t } = useTranslation('login')
  const { loginValidationSchema } = useLoginValidation()
  const { submitForm, loading } = useSubmitForm(onSuccess)

  return (
    <BaseForm initialValues={loginInitialValues} validationSchema={loginValidationSchema} onSubmit={submitForm}>
      <Stack spacing={2} sx={{ width: '100%' }}>
        {method === 'phone' ? (
          <Stack direction="row" spacing={1}>
            <CountryCodeSelect name="countryCode" type="tel" placeholder={t('logininput.phone')} size="small" />
            <CustomFormikField name="identifier" type="tel" placeholder={t('logininput.phone')} size="small" sx={{ ...formTextFieldSx, flex: 1 }} />
          </Stack>
        ) : (
          <CustomFormikField
            name="identifier"
            type="email"
            placeholder={t('logininput.email')}
            size="small"
            sx={formTextFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineRoundedIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        )}
        <PasswordInput name="password" placeholder={t('logininput.password')} size="small" sx={formTextFieldSx} />
        <Button component={Link} to="/reset-password" state={{ method }} onClick={onSuccess} sx={{ alignSelf: 'flex-start', p: 0, minHeight: 0, ...switchButtonSx }}>
          {t('forgot')}
        </Button>
        <SubmitButton disabled={loading}>{t('login')}</SubmitButton>
        <Button type="button" onClick={() => setMethod(method === 'email' ? 'phone' : 'email')} sx={switchButtonSx}>
          {method === 'email' ? t('loginMethod.phone') : t('loginMethod.email')}
        </Button>
        <Button type="button" startIcon={<ArrowBackRoundedIcon />} onClick={onBack} sx={{ color: '#071F28', textTransform: 'none', fontWeight: 800 }}>
          Back to login options
        </Button>
      </Stack>
    </BaseForm>
  )
}

const SignupTermsLabel = ({ t, onNavigate }) => (
  <Typography variant="body2" sx={{ color: '#071F28', lineHeight: 1.45 }}>
    {t('signUpinput.agree')}{' '}
    <Box component={Link} to="/terms-of-service" onClick={onNavigate} sx={{ color: '#FE1F02', fontWeight: 800, textDecoration: 'underline' }}>
      {t('signUpinput.terms')}
    </Box>{' '}
    <Box component={Link} to="/privacy-policy" onClick={onNavigate} sx={{ color: '#FE1F02', fontWeight: 800, textDecoration: 'underline' }}>
      {t('signUpinput.privacy')}
    </Box>
  </Typography>
)

const AuthSignupForm = ({ method, setMethod, onBack, onSuccess }) => {
  const { t } = useTranslation('signup')
  const { signupValidationSchema } = useSignUpValidation()
  const { signupSubmit, loading, pendingPhoneNumber, chooseChannel, cancelChannelPicker } = useSignupSubmit(onSuccess)

  return (
    <>
      <BaseForm initialValues={signupInitialValues} validationSchema={signupValidationSchema} onSubmit={signupSubmit}>
        <Stack spacing={2} sx={{ width: '100%' }}>
          <CustomFormikField
            name="firstName"
            placeholder={t('signUpinput.firstName')}
            icon={<PersonOutlineIcon sx={{ fontSize: 18 }} />}
            size="small"
            sx={formTextFieldSx}
          />
          <CustomFormikField
            name="lastName"
            placeholder={t('signUpinput.lastName')}
            icon={<PersonOutlineIcon sx={{ fontSize: 18 }} />}
            size="small"
            sx={formTextFieldSx}
          />
          <UsernameField
            placeholder={t('signUpinput.username')}
            icon={<PermContactCalendarIcon sx={{ fontSize: 18 }} />}
            size="small"
            sx={formTextFieldSx}
          />
          {method === 'phone' ? (
            <Stack direction="row" spacing={1}>
              <CountryCodeSelect name="countryCode" type="tel" placeholder={t('signUpinput.phone')} size="small" />
              <CustomFormikField name="identifier" type="tel" placeholder={t('signUpinput.phone')} size="small" sx={{ ...formTextFieldSx, flex: 1 }} />
            </Stack>
          ) : (
            <CustomFormikField
              name="identifier"
              type="email"
              placeholder={t('signUpinput.email')}
              icon={<MailOutlineRoundedIcon sx={{ fontSize: 18 }} />}
              size="small"
              sx={formTextFieldSx}
            />
          )}
          <PasswordInput name="password" placeholder={t('signUpinput.password')} size="small" sx={formTextFieldSx} />
          <CheckboxFormik name="terms" Label={<SignupTermsLabel t={t} onNavigate={onSuccess} />} />
          <SubmitButton disabled={loading}>{t('create-account')}</SubmitButton>
          <Button type="button" onClick={() => setMethod(method === 'email' ? 'phone' : 'email')} sx={switchButtonSx}>
            {method === 'email' ? t('signUpMethod.phone') : t('signUpMethod.email')}
          </Button>
          <Button type="button" startIcon={<ArrowBackRoundedIcon />} onClick={onBack} sx={{ color: '#071F28', textTransform: 'none', fontWeight: 800 }}>
            Back to signup options
          </Button>
        </Stack>
      </BaseForm>
      <ChannelPickerDialog
        open={Boolean(pendingPhoneNumber)}
        phoneNumber={pendingPhoneNumber}
        onSelect={chooseChannel}
        onClose={cancelChannelPicker}
      />
    </>
  )
}

export const AuthDialog = ({ open, mode = 'login', onClose, onModeChange }) => {
  const { t } = useTranslation('header')
  const isSignupMode = mode === 'signup'
  const [authStep, setAuthStep] = useState('choices')
  const [formMethod, setFormMethod] = useState('email')

  useEffect(() => {
    if (open) {
      setAuthStep('choices')
      setFormMethod('email')
    }
  }, [mode, open])

  const openForm = (method) => {
    setFormMethod(method)
    setAuthStep('form')
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '6px',
          width: { xs: 'calc(100% - 30px)', sm: 550 },
          maxHeight: 'calc(100dvh - 30px)',
          overflow: 'auto',
          backgroundColor: '#fff',
          boxShadow: '0 28px 80px rgba(0,0,0,0.38)',
        },
      }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.76)' } }}
    >
      <IconButton
        aria-label={t('auth.close')}
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          color: '#2C3338',
          '&:hover': { backgroundColor: '#F4F6F7' },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 30 }} />
      </IconButton>

      <Stack alignItems="center" sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 6.8, sm: 7.5 }, pb: { xs: 3.5, sm: 4 } }}>
        <Box component="img" src={logo} alt="Direct Rent" sx={{ width: { xs: 150, sm: 170 }, mb: { xs: 3.8, sm: 4.3 } }} />

        <Typography
          component="h2"
          sx={{
            color: '#071F28',
            fontSize: { xs: 24, sm: 28 },
            fontWeight: 900,
            lineHeight: 1.22,
            textAlign: 'center',
            mb: { xs: 3.5, sm: 4.2 },
          }}
        >
          {t(isSignupMode ? 'auth.signupTitle' : 'auth.loginTitle')}
        </Typography>

        {authStep === 'form' ? (
          isSignupMode ? (
            <AuthSignupForm method={formMethod} setMethod={setFormMethod} onBack={() => setAuthStep('choices')} onSuccess={onClose} />
          ) : (
            <AuthLoginForm method={formMethod} setMethod={setFormMethod} onBack={() => setAuthStep('choices')} onSuccess={onClose} />
          )
        ) : (
          <Stack spacing={1.2} sx={{ width: '100%' }}>
          {isSignupMode ? (
            <>
              <GoogleLoginButton
                variant="outlined"
                buttonSx={googleChoiceButtonSx}
                startIcon={<GoogleMark />}
                onSuccess={onClose}
              >
                {t('auth.joinGoogle')}
              </GoogleLoginButton>
              <FacebookLoginButton
                variant="outlined"
                buttonSx={authChoiceButtonSx}
                startIcon={<FacebookRoundedIcon sx={{ color: '#1877F2', fontSize: 25 }} />}
                onSuccess={onClose}
              >
                {t('auth.joinFacebook')}
              </FacebookLoginButton>
              <AppleLoginButton
                variant="outlined"
                buttonSx={authChoiceButtonSx}
                startIcon={<AppleIcon sx={{ color: '#000', fontSize: 25 }} />}
                onSuccess={onClose}
              >
                {t('auth.joinApple')}
              </AppleLoginButton>
            </>
          ) : (
            <>
              <Button
                type="button"
                onClick={() => openForm('phone')}
                variant="outlined"
                startIcon={<LocalPhoneRoundedIcon sx={{ color: '#FE1F02', fontSize: 25 }} />}
                sx={authChoiceButtonSx}
              >
                {t('auth.phone')}
              </Button>
              <Button
                type="button"
                onClick={() => openForm('email')}
                variant="outlined"
                startIcon={<MailOutlineRoundedIcon sx={{ color: '#FE1F02', fontSize: 25 }} />}
                sx={authChoiceButtonSx}
              >
                {t('auth.email')}
              </Button>
            </>
          )}

          <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', py: 2 }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#D0D7DE' }} />
            <Typography sx={{ color: '#071F28', fontSize: 16, fontWeight: 500 }}>{t('auth.or')}</Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#D0D7DE' }} />
          </Stack>

          {isSignupMode ? (
            <>
              <Button
                type="button"
                onClick={() => openForm('email')}
                variant="outlined"
                startIcon={<MailOutlineRoundedIcon sx={{ color: '#FE1F02', fontSize: 25 }} />}
                sx={authChoiceButtonSx}
              >
                {t('auth.joinEmail')}
              </Button>
              <Button
                type="button"
                onClick={() => openForm('phone')}
                variant="outlined"
                startIcon={<LocalPhoneRoundedIcon sx={{ color: '#FE1F02', fontSize: 25 }} />}
                sx={authChoiceButtonSx}
              >
                {t('auth.joinPhone')}
              </Button>
            </>
          ) : (
            <>
              <GoogleLoginButton
                variant="outlined"
                buttonSx={googleChoiceButtonSx}
                startIcon={<GoogleMark />}
                onSuccess={onClose}
              >
                {t('auth.google')}
              </GoogleLoginButton>
              <FacebookLoginButton
                variant="outlined"
                buttonSx={authChoiceButtonSx}
                startIcon={<FacebookRoundedIcon sx={{ color: '#1877F2', fontSize: 25 }} />}
                onSuccess={onClose}
              >
                {t('auth.facebook')}
              </FacebookLoginButton>
              <AppleLoginButton
                variant="outlined"
                buttonSx={authChoiceButtonSx}
                startIcon={<AppleIcon sx={{ color: '#000', fontSize: 25 }} />}
                onSuccess={onClose}
              >
                {t('auth.apple')}
              </AppleLoginButton>
            </>
          )}
          </Stack>
        )}

        {isSignupMode && authStep === 'choices' && (
          <Typography sx={{ mt: 2.1, color: '#071F28', fontSize: { xs: 14.5, sm: 16 }, lineHeight: 1.45, textAlign: 'center' }}>
            {t('auth.termsPrefix')}{' '}
            <Box component={Link} to="/terms-of-service" onClick={onClose} sx={{ color: '#071F28', fontWeight: 800, textDecoration: 'underline' }}>
              {t('auth.terms')}
            </Box>{' '}
            {t('auth.and')}{' '}
            <Box component={Link} to="/privacy-policy" onClick={onClose} sx={{ color: '#071F28', fontWeight: 800, textDecoration: 'underline' }}>
              {t('auth.privacy')}
            </Box>
          </Typography>
        )}

        <Button
          type="button"
          onClick={() => {
            setAuthStep('choices')
            setFormMethod('email')
            onModeChange(isSignupMode ? 'login' : 'signup')
          }}
          sx={{
            mt: { xs: 3, sm: 3.4 },
            color: '#FE1F02',
            fontSize: { xs: 16, sm: 18 },
            fontWeight: 900,
            textTransform: 'none',
            '&:hover': { backgroundColor: 'transparent', color: '#D91B04' },
          }}
        >
          {isSignupMode
            ? `${t('auth.alreadyHave')} ${t('auth.login')}`
            : `${t('auth.newTo')} ${t('auth.createAccount')}`}
        </Button>

      </Stack>
    </Dialog>
  )
}
